/** The stints down the left-hand side, newest first.
 *
 * Each row is a real button, not a div that listens for clicks: choosing a
 * session is a control, it belongs in the tab order, and it should answer the
 * space bar. The chosen one takes an accent ring rather than a tint, which is
 * the design system's rule for a card that is itself a control — the surface
 * is already the only light in the row and tinting it would leave nothing to
 * ring.
 *
 * The thumbnail is the design's stylised loop rather than the circuit's own
 * geometry, and knowingly so: a session carries no survey data, and fetching
 * the compiled outline of every circuit in the list to draw it at fifty-six
 * pixels would be a lot of megabytes spent on a silhouette. The selected row's
 * stroke turns accent, which is what the thumbnail is actually for.
 *
 * On a phone the list stacks above the session it chose, so every stint
 * stood between the visitor and the laps they picked. Below the `md`
 * breakpoint it folds to the chosen row and one toggle; choosing a session
 * from the open list folds it again.
 */

import { useState } from 'react';

import { PLACEHOLDER_PATH } from '@/components/TrackOutline';
import { lapTime, plural, sessionWhen } from '@/lib/format';
import { syncColour, syncState } from '@/components/sessions/derive';
import { visibilityOf } from '@/components/sessions/share';
import type { DriveSession } from '@/lib/types';
import { useMedia } from '@/lib/useMedia';

export interface SessionListProps {
  sessions: readonly DriveSession[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Lap documents the service holds for the selected session, once they have
   *  been read. Every other row is judged on whether its drive has ended. */
  heldLaps: number | null;
}

function Thumb({ selected }: { selected: boolean }) {
  return (
    <svg
      viewBox="0 0 400 260"
      width="56"
      height="36"
      style={{ alignSelf: 'center' }}
      aria-hidden="true"
    >
      <path
        d={PLACEHOLDER_PATH}
        fill="none"
        strokeWidth="16"
        strokeLinejoin="round"
        stroke={selected ? 'var(--color-accent)' : 'var(--color-neutral-600)'}
      />
    </svg>
  );
}

export function SessionList({ sessions, selectedId, onSelect, heldLaps }: SessionListProps) {
  const shared = sessions.filter((session) => visibilityOf(session) !== 'private').length;
  const wide = useMedia('(min-width: 768px)');
  const [open, setOpen] = useState(false);

  // Folded, the chosen row stands alone; before anything is chosen, the first.
  const folds = !wide;
  const chosen = sessions.find((session) => session.id === selectedId) ?? sessions[0];
  const shown = folds && !open ? (chosen ? [chosen] : []) : sessions;

  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Sessions</h1>
        <span
          className="tabular-nums"
          style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}
        >
          {sessions.length} · {shared > 0 ? `${shared} shared` : 'private'}
        </span>
      </div>

      {shown.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          selected={session.id === selectedId}
          heldLaps={heldLaps}
          onSelect={(id) => {
            onSelect(id);
            setOpen(false);
          }}
        />
      ))}

      {folds && sessions.length > 1 ? (
        <button
          type="button"
          className="btn btn-ghost"
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            color: 'var(--color-neutral-500)',
          }}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          {open ? 'Hide other sessions' : `All ${sessions.length} sessions`}
        </button>
      ) : null}

      {folds && !open ? null : (
        <p
          style={{
            fontSize: 11,
            color: 'var(--color-neutral-600)',
            margin: '6px 0 0',
            textWrap: 'pretty',
          }}
        >
          Your own driving, private by default. Racing lines here stay with your session and never
          feed a track outline. Drivers who made a session public on this circuit appear under
          &ldquo;compare with&rdquo;.
        </p>
      )}
    </aside>
  );
}

function SessionRow({
  session,
  selected,
  heldLaps,
  onSelect,
}: {
  session: DriveSession;
  selected: boolean;
  heldLaps: number | null;
  onSelect: (id: string) => void;
}) {
  const state = syncState(session, selected ? heldLaps : null);
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(session.id)}
      style={{
        display: 'grid',
        gridTemplateColumns: '56px minmax(0, 1fr)',
        gap: 12,
        width: '100%',
        padding: '10px 12px',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        border: 0,
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface)',
        boxShadow: selected ? '0 0 0 1px var(--color-accent)' : 'var(--shadow-sm)',
      }}
    >
      <Thumb selected={selected} />
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
        }}
      >
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 13.5,
          }}
        >
          <span className="truncate">
            {session.track_name || session.official_id || 'Unnamed circuit'}
          </span>
          <span className="tabular-nums" style={{ color: 'var(--color-accent-300)' }}>
            {lapTime(session.best_lap_ms)}
          </span>
        </span>
        <span className="truncate" style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}>
          {session.car || 'car not recorded'}
        </span>
        <span
          className="tabular-nums"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 11,
            color: 'var(--color-neutral-600)',
          }}
        >
          <span className="truncate">
            {sessionWhen(session.started_at)} · {plural(session.laps, 'lap')}
          </span>
          <span style={{ color: syncColour(state), whiteSpace: 'nowrap' }}>{state.label}</span>
        </span>
      </span>
    </button>
  );
}

export default SessionList;
